import { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "../utils/api";
import { invalidateBranding } from "../utils/branding";
import { resetProfile } from "./useProfile";

const STORAGE_KEY = "auth-user";
export const AUTH_EXPIRED_EVENT = "auth-expired";

/** All data of the logged-in user from the browser. The theme (light/dark) is a device setting and stays. */
function clearClientSession() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.clear();
  } catch {
    /* storage is unavailable */
  }
  invalidateBranding();
  resetProfile();
}

/**
 * The logged-in user is remembered in localStorage (a refresh does not log out), but the server session
 * is the only real source of truth:
 *  - logout calls /logout.php (destroys the session and cookie) and clears all local data;
 *  - /session_status.php is checked on start and when the tab is shown again;
 *  - if any call returns "Niste prijavljeni" (auth-expired event), the user is logged out locally.
 * When there is no user, App shows the login at ANY address (no 404 on refresh at /settings).
 */
export function useAuth() {
  const [user, setUser] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (user) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore if localStorage is unavailable
    }
  }, [user]);

  const endLocally = useCallback(() => {
    clearClientSession();
    try {
      window.history.replaceState(null, "", "/");
    } catch {
      /* irrelevant */
    }
    setUser(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/logout.php`, { method: "POST", credentials: "include", cache: "no-store" });
    } catch {
      /* even if the server does not respond, the local session is cleared */
    }
    endLocally();
  }, [endLocally]);

  // The server ended the session (expired, logout in another tab): straight back to the login.
  useEffect(() => {
    if (!user) return undefined;
    const onExpired = () => endLocally();
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);

    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/session_status.php`, { credentials: "include", cache: "no-store" });
        const json = await res.json();
        if (!cancelled && json && json.success === false) endLocally();
      } catch {
        /* never mind: do not log the user out because of a network error */
      }
    };
    check();
    const onVisible = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(user), endLocally]);

  return { user, login: setUser, logout };
}
